export const BLOCK_EDITOR_VALUE_HISTORY_LIMIT = 32;
export const BLOCK_EDITOR_VALUE_HISTORY_TTL_MS = 2_000;

export interface PendingBlockEditorValue {
  value: string;
  emittedAt: number;
}

export interface BlockEditorValueSyncState {
  currentValue: string;
  pendingInternalValues: readonly PendingBlockEditorValue[];
}

export interface BlockEditorValueReceipt {
  source: "internal" | "external";
  state: BlockEditorValueSyncState;
}

export function createBlockEditorValueSyncState(
  initialValue: string
): BlockEditorValueSyncState {
  return {
    currentValue: initialValue,
    pendingInternalValues: [],
  };
}

export function recordBlockEditorValueEmission(
  state: BlockEditorValueSyncState,
  value: string,
  now = Date.now()
): BlockEditorValueSyncState {
  if (value === state.currentValue) return state;

  return {
    currentValue: value,
    pendingInternalValues: [
      ...activePendingValues(state.pendingInternalValues, now),
      { value, emittedAt: now },
    ].slice(-BLOCK_EDITOR_VALUE_HISTORY_LIMIT),
  };
}

export function receiveBlockEditorValue(
  state: BlockEditorValueSyncState,
  value: string,
  now = Date.now()
): BlockEditorValueReceipt {
  const pendingInternalValues = activePendingValues(
    state.pendingInternalValues,
    now
  );
  const matchedIndex = pendingInternalValues.findIndex(
    (pendingValue) => pendingValue.value === value
  );

  if (matchedIndex !== -1) {
    return {
      source: "internal",
      state: {
        ...state,
        pendingInternalValues: pendingInternalValues.filter(
          (_, index) => index !== matchedIndex
        ),
      },
    };
  }

  if (value === state.currentValue) {
    return {
      source: "internal",
      state: { ...state, pendingInternalValues },
    };
  }

  return {
    source: "external",
    state: {
      currentValue: value,
      pendingInternalValues: [],
    },
  };
}

function activePendingValues(
  pendingValues: readonly PendingBlockEditorValue[],
  now: number
): PendingBlockEditorValue[] {
  return pendingValues.filter(
    ({ emittedAt }) => now - emittedAt <= BLOCK_EDITOR_VALUE_HISTORY_TTL_MS
  );
}
